import { audienceFromStatus, chromeFor, hostPhrase } from "./catalogueAudience";
import {
  fetchClientLikes,
  openCatalogueSession,
  setClientLike,
} from "../../../components/catalogue/catalogueResellerApi";
import { rememberMediaResponse } from "../../../media/mediaReferences";
import React, { useEffect, useState, useCallback } from "react";
import HeaderEcomerce from "./HeaderEcomerce";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { getProducts } from "../../../Store/products/actions";
import CatalogueProductInfo from "./CatalogueProductInfo";
import SharedCatalogueChrome from "./SharedCatalogueChrome";
import PublicCatalogueAuthGate from "./PublicCatalogueAuthGate";
import CatalogueOpenFailed from "./CatalogueOpenFailed";
import { CatalogueVisitorProvider, useCatalogueVisitor } from "./CatalogueVisitorContext";
import "./sharedCatalogue.css";
import { mapProductToDetailForm } from "./mapProductToDetailForm";
import {
  buildPublicAuthHeaders,
  clearPublicCatalogueSession,
  legacyCatalogueAccessToken,
  linkTokenFromLegacySession,
  loadPublicCatalogueGuest,
  loadPublicCatalogueSession,
  savePublicCatalogueGuest,
  savePublicCatalogueSession,
  pageFromSearch,
  withCataloguePage,
  withFocusPiece,
} from "./publicCatalogueHelpers";
import notifications from "../../../common/Components/Notifications/Notification";
import { rememberLastVisitedCatalogue } from "./catalogueVisitorSession";
import SharedCatalogueAuthActions from "./SharedCatalogueAuthActions";
import { attachVisitorFromCatalogueSession, type VisitorAuthPayload } from "./catalogueVisitorClient";
import type { PublicCatalogueStatus } from "../../../types/catalogue.types";
import { usePublicCatalogueAnalytics } from "./usePublicCatalogueAnalytics";
import { useCatalogueEngagement } from "./useCatalogueEngagement";
import { viewerGateNeeded } from "../../../components/catalogue/catalogueViewerGate";
import { fetchPublicCatalogueStatus } from "../../../components/catalogue/catalogueSecurityApi";
import CatalogueProtection from "./CatalogueProtection";

const emptyForm = {
  productUniqueId: "",
  artform: "",
  type: "",
  category: "",
  subCategory: "",
  weaveType: "",
  productName: "",
  designer: "",
  designerMedia: [] as any[],
  weaver: "",
  weaverMedia: [] as any[],
  dyer: "",
  dyerMedia: [] as any[],
  finisher: "",
  finisherMedia: [] as any[],
  profileURL: "",
  tags: [],
  variants: [] as any[],
  yarnType: "",
  workingHours: "",
  loomNum: "",
  color: "",
  productOwner: "",
  sellPrice: "",
  costPrice: "",
  designNum: "",
  processSteps: [] as { title: string; text: string }[],
  flexibleUploadProcesses: [] as any[],
};

const PublicCatalogueProductInfoContent = (props: any) => {
  const { getProducts } = props;
  const history = useHistory();
  const { isVisitorSession, openAuthModal, signOutVisitor, signInVisitor, profile } = useCatalogueVisitor();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const productId =
    params.id || window.location.pathname.split("/").pop() || "";

  const searchParams = new URLSearchParams(location.search);
  const catalogueId = searchParams.get("catalogueId") || undefined;
  const legacySessionToken = searchParams.get("session") || undefined;
  const linkToken =
    searchParams.get("link") ||
    linkTokenFromLegacySession(catalogueId, legacySessionToken) ||
    undefined;
  const accessToken = legacyCatalogueAccessToken(searchParams);
  const [storedSessionToken, setStoredSessionToken] = useState<string | undefined>(() =>
    loadPublicCatalogueSession(catalogueId, linkToken)
  );
  const [guest, setGuest] = useState(() => loadPublicCatalogueGuest(catalogueId, linkToken));
  const sessionToken = storedSessionToken;
  const priceTier = searchParams.get("priceTier") || undefined;

  const [form, setForm]: any = useState({ ...emptyForm });
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<PublicCatalogueStatus>({});
  const [showAccessGate, setShowAccessGate] = useState(false);
  const [openFailReason, setOpenFailReason] = useState<string | undefined>();
  const audience = audienceFromStatus(meta);
  const chrome = chromeFor(audience, meta, loading);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    if (audience !== "client" || !catalogueId || !linkToken || !sessionToken) return;
    let cancelled = false;
    fetchClientLikes(catalogueId, linkToken, sessionToken).then((ids) => {
      if (!cancelled) setLiked(ids.includes(productId));
    });
    return () => {
      cancelled = true;
    };
  }, [audience, catalogueId, linkToken, sessionToken, productId]);

  const toggleLike = async () => {
    if (!catalogueId || !linkToken) return;
    const next = !liked;
    setLiked(next);
    const ok = await setClientLike(catalogueId, linkToken, sessionToken, productId, next);
    if (!ok) setLiked(!next);
  };

  usePublicCatalogueAnalytics({
    catalogueId: catalogueId || "",
    linkToken,
    organizationId: meta?.organizationId,
    studioMeasurementId: meta?.googleAnalyticsMeasurementId,
  });
  // This piece and the time on it, per viewer session. A studio preview is never counted.
  useCatalogueEngagement({
    catalogueId,
    linkToken,
    sessionToken,
    productUniqueId: productId,
    enabled: Boolean(meta?.engagementTracking) && !meta?.studioPreview,
  });

  const backToCollection = () => {
    if (!catalogueId) {
      history.goBack();
      return;
    }
    const p = new URLSearchParams();
    if (linkToken) p.set("link", linkToken);
    if (accessToken) p.set("token", accessToken);
    if (priceTier) p.set("priceTier", priceTier);
    for (const key of ["minPrice", "maxPrice", "tags"]) {
      const v = searchParams.get(key);
      if (v) p.set(key, v);
    }
    const q = withFocusPiece(
      withCataloguePage(p, pageFromSearch(location.search)),
      productId
    ).toString();
    history.push(`/catalogue-products/${catalogueId}${q ? `?${q}` : ""}`);
  };

  const loadViaPublicCatalogue = useCallback(
    async (opts?: { statusData?: PublicCatalogueStatus }) => {
      if (!accessToken && !opts?.statusData?.studioPreview && (!linkToken || !sessionToken)) {
        setShowAccessGate(true);
        setForm({ ...emptyForm });
        return;
      }
      const base = process.env.REACT_APP_SERVERURL || "";
      const qs = new URLSearchParams();
      if (accessToken) qs.set("accessToken", accessToken);
      if (linkToken) qs.set("link", linkToken);
      if (priceTier) qs.set("priceTier", priceTier);
      const url = `${base}/catalouge/public/${catalogueId}/product/${encodeURIComponent(
        productId
      )}?${qs.toString()}`;
      const headers: Record<string, string> = { ...(buildPublicAuthHeaders() as Record<string, string>) };
      if (sessionToken) {
        headers["X-HKP-Catalogue-Session"] = sessionToken;
      }
      const res = await fetch(url, { headers });
      const json = rememberMediaResponse(await res.json());
      if (!json?.success || !json?.data?.product) {
        const msg = String(json?.message || "").toLowerCase();
        const accessLike =
          msg.includes("access denied") ||
          msg.includes("password") ||
          msg.includes("share link") ||
          msg.includes("otp") ||
          msg.includes("session");
        if (accessLike) {
          clearPublicCatalogueSession(catalogueId, linkToken);
          setStoredSessionToken(undefined);
          setShowAccessGate(true);
          setForm({ ...emptyForm });
          return;
        }
        setShowAccessGate(false);
        notifications({
          title: "Product",
          message: json?.message || "Could not load this product.",
          type: "danger",
        });
        setForm({ ...emptyForm });
        return;
      }
      setShowAccessGate(false);
      setForm({
        ...mapProductToDetailForm(json.data.product, undefined),
        family: Array.isArray(json.data.family) ? json.data.family : [],
      });
    },
    [catalogueId, productId, accessToken, sessionToken, linkToken, priceTier]
  );

  useEffect(() => {
    setStoredSessionToken(loadPublicCatalogueSession(catalogueId, linkToken));
  }, [catalogueId, linkToken, location.search]);

  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        if (catalogueId) {
          let statusData: PublicCatalogueStatus | undefined;
          try {
            const sj = await fetchPublicCatalogueStatus(catalogueId, linkToken);
            if (!cancelled && sj?.success && sj?.data) {
              statusData = sj.data;
              setMeta(sj.data);
            }
          } catch {
            /* ignore */
          }
          // OPEN sharing mode: a product deep link opens without the code, like the collection page.
          if (
            !cancelled &&
            statusData?.openAccess &&
            linkToken &&
            !sessionToken &&
            statusData.linkValid !== false
          ) {
            if (viewerGateNeeded(statusData.viewerGate)) {
              // The link asks who is viewing first; that form lives on the collection page,
              // which brings them back to this piece.
              const p = new URLSearchParams();
              p.set("link", linkToken);
              if (priceTier) p.set("priceTier", priceTier);
              const q = withFocusPiece(p, productId).toString();
              history.replace(`/catalogue-products/${catalogueId}${q ? `?${q}` : ""}`);
              return;
            }
            const opened = await openCatalogueSession(catalogueId, linkToken).catch(() => null);
            if (!cancelled && opened?.success && opened.data?.sessionToken) {
              savePublicCatalogueSession(catalogueId, linkToken, opened.data.sessionToken);
              // The new session re-runs this effect through loadViaPublicCatalogue.
              setStoredSessionToken(opened.data.sessionToken);
              return;
            }
            if (!cancelled) setOpenFailReason(opened?.message);
          }
          if (!cancelled)
            await loadViaPublicCatalogue({ statusData });
        } else {
          await new Promise<void>((resolve) => {
            getProducts(
              {
                offset: 0,
                limit: 10,
                ProductId: productId,
              },
              (res: any) => {
                if (cancelled) {
                  resolve();
                  return;
                }
                if (res?.data?.docs?.length) {
                  setForm(
                    mapProductToDetailForm(res.data.docs[0], undefined)
                  );
                } else {
                  notifications({
                    title: "Product",
                    message: res?.message || "Product not found.",
                    type: "danger",
                  });
                  setForm({ ...emptyForm });
                }
                resolve();
              }
            );
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [productId, catalogueId, loadViaPublicCatalogue, getProducts]);

  const useNewChrome = Boolean(catalogueId);

  useEffect(() => {
    if (catalogueId && !showAccessGate && !loading) {
      rememberLastVisitedCatalogue(catalogueId, location.search.replace(/^\?/, ""));
    }
  }, [catalogueId, showAccessGate, loading, location.search]);

  const visitorHeaderActions = (
    <SharedCatalogueAuthActions
      // A reseller's client sees their own invite name, never a studio visitor account on this browser.
      isVisitorSession={audience === "client" ? false : isVisitorSession}
      visitorName={profile?.name}
      guestName={guest?.displayName}
      catalogueUnlocked={Boolean(sessionToken)}
      onMySelections={() => history.push("/catalogue-visitor/account")}
      onOpenAuth={() => {
        void (async () => {
          if (isVisitorSession) {
            history.push("/catalogue-visitor/account");
            return;
          }
          if (sessionToken && catalogueId) {
            const payload = await attachVisitorFromCatalogueSession(catalogueId, sessionToken);
            if (payload) {
              signInVisitor(payload.token, payload.profile);
              history.push("/catalogue-visitor/account");
              return;
            }
          }
          openAuthModal();
        })();
      }}
      onSignOutVisitor={() => signOutVisitor()}
      hideAccount={audience === "client"}
      onLeaveCollection={() => {
        clearPublicCatalogueSession(catalogueId, linkToken);
        setStoredSessionToken(undefined);
        setGuest(undefined);
      }}
    />
  );

  const onAuthUnlocked = (
    newSession: string,
    guestInfo?: { displayName?: string; phone?: string },
    visitor?: VisitorAuthPayload
  ) => {
    if (catalogueId && linkToken) {
      savePublicCatalogueSession(catalogueId, linkToken, newSession);
      savePublicCatalogueGuest(catalogueId, linkToken, {
        displayName: guestInfo?.displayName || "Verified guest",
        phone: guestInfo?.phone,
      });
      setGuest(loadPublicCatalogueGuest(catalogueId, linkToken));
    }
    setStoredSessionToken(newSession);
    const p = new URLSearchParams(location.search);
    p.delete("session");
    const nextSearch = p.toString();
    history.replace(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`);
    if (visitor?.token) {
      signInVisitor(visitor.token, visitor.profile);
      if (catalogueId) {
        rememberLastVisitedCatalogue(catalogueId, nextSearch);
      }
    }
  };

  if (useNewChrome) {
    return (
      <SharedCatalogueChrome
        organizationName={
          audience === "client"
            ? chrome.hostName
            : meta.organizationName || meta.catalogueName || "Collection"
        }
        subtitle={meta.catalogueName || "Product detail"}
        pillText="Private link"
        mark={chrome.mark}
        footerText={audience === "client" ? chrome.footer : undefined}
        headerActions={visitorHeaderActions}
      >
        <button
          type="button"
          className="hkp-shared-back"
          onClick={backToCollection}
        >
          ← Back to collection
        </button>
        {showAccessGate && catalogueId && meta.openAccess ? (
          // An open link has no invite password; reaching the gate means it failed to open.
          <CatalogueOpenFailed reason={openFailReason} />
        ) : showAccessGate && catalogueId ? (
          <PublicCatalogueAuthGate
            catalogueId={catalogueId}
            catalogueTitle={meta.catalogueName}
            unlockStatus={meta}
            onUnlocked={onAuthUnlocked}
            hostLabel={hostPhrase(audience, meta)}
          />
        ) : (
          <div className="hkp-shared-detail-wrap">
            {loading ? (
              <div className="hkp-shared-muted">Loading product…</div>
            ) : (
              <CatalogueProductInfo
                form={form}
                catalogueContext={{
                  catalogueId: catalogueId!,
                  routeProductId: productId,
                  shareToken: accessToken,
                  linkToken: linkToken,
                  sessionToken: sessionToken,
                  priceTier: priceTier,
                  guestName: guest?.displayName,
                  guestPhone: guest?.phone,
                  audience,
                  curatorName: meta.curatorName,
                  curatorWhatsApp: meta.curatorWhatsApp,
                  studioWhatsApp: meta.studioWhatsApp,
                  allowDownload: Boolean(meta.allowDownload),
                  liked,
                  onToggleLike: () => void toggleLike(),
                }}
              />
            )}
          </div>
        )}
        {!showAccessGate && !loading ? (
          <CatalogueProtection status={meta} viewerName={profile?.name || guest?.displayName || meta.recipientName} />
        ) : null}
      </SharedCatalogueChrome>
    );
  }

  return (
    <>
      <HeaderEcomerce {...props} route={"publicRoute"} />
      <br /> <br /> <br /> <br />
      {loading ? (
        <div className="hkp-shared-muted">Loading product…</div>
      ) : (
        <CatalogueProductInfo form={form} />
      )}
    </>
  );
};

const PublicCatalogueProductInfoWrapper = (props: any) => {
  const location = useLocation();
  const cid = new URLSearchParams(location.search).get("catalogueId") || undefined;
  return (
    <CatalogueVisitorProvider catalogueId={cid}>
      <PublicCatalogueProductInfoContent {...props} />
    </CatalogueVisitorProvider>
  );
};

const PublicCatalogueProductInfo = (props: any) => <PublicCatalogueProductInfoWrapper {...props} />;

const mapStateToProps = (state: any) => ({
  ...state,
});

export default connect(mapStateToProps, {
  getProducts,
})(PublicCatalogueProductInfo);

